import {
  CloudArrowUpIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DragEvent, useEffect, useRef, useState } from 'react';

import { CompanionMatterPicker } from '@/components/shared/CompanionMatterPicker';
import { Button } from '@/components/ui/catalyst/button';
import {
  Checkbox,
  CheckboxField,
} from '@/components/ui/catalyst/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Label } from '@/components/ui/catalyst/fieldset';
import { CollectionNode } from '@/services/types/collection-types';
import { CompanionMember } from '@/services/types/companions-types';

interface UploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  collectionTree: CollectionNode;
  onUpload: (
    files: File[],
    collectionId: string,
    autoSplitLarge: boolean,
    uploadToCompanions: boolean,
    targetCompanionMatterIds?: string[],
  ) => Promise<string[] | void>;
  companions?: CompanionMember[];
}

export const UploadDialog = ({
  isOpen,
  onClose,
  collectionId,
  collectionTree,
  onUpload,
  companions = [],
}: UploadDialogProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] =
    useState(collectionId);
  const [isUploading, setIsUploading] = useState(false);
  const [autoSplitLarge, setAutoSplitLarge] = useState(false);
  const [selectedCompanionMatterIds, setSelectedCompanionMatterIds] = useState<
    string[]
  >([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if this matter has companions (exclude current matter)
  const selectableCompanions = companions.filter(
    (c) => c.id !== collectionTree.matter_id,
  );
  const hasCompanions = selectableCompanions.length > 0;

  useEffect(() => {
    setSelectedCollectionId(collectionId);
  }, [collectionId]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setIsUploading(false);
      setSelectedCompanionMatterIds([]);
    }
  }, [isOpen]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(files);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles([...selectedFiles, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadToCompanions = selectedCompanionMatterIds.length > 0;

  const isUploadDisabled = selectedFiles.length === 0 || isUploading;

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      await onUpload(
        selectedFiles,
        selectedCollectionId,
        autoSplitLarge,
        uploadToCompanions,
        uploadToCompanions ? selectedCompanionMatterIds : undefined,
      );
      onClose();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Function to render collection options recursively
  const renderCollectionOptions = (node: CollectionNode, level = 0) => {
    const options = [];
    const prefix = '\u00A0'.repeat(level * 2);

    options.push(
      <option key={node.id} value={node.id}>
        {prefix + (node.name === 'root' ? 'All Files' : node.name)}
      </option>,
    );

    if (node.children) {
      node.children.forEach((child) => {
        options.push(...renderCollectionOptions(child, level + 1));
      });
    }

    return options;
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Upload Files</DialogTitle>
      <DialogDescription>
        Select files to upload to the selected folder.
      </DialogDescription>
      <DialogBody>
        {hasCompanions && (
          <div className="mb-4 flex gap-1.5 rounded-md bg-zinc-50 px-3 py-2 border border-zinc-200 dark:bg-zinc-800 dark:border-zinc-700">
            <InformationCircleIcon className="h-4 w-4 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-zinc-600 dark:text-zinc-300">
              This matter is part of a companion group. Consider uploading this
              document to related matters as well.
            </p>
          </div>
        )}

        <div className="mb-4">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="destination-folder-select"
            className="block text-sm font-medium mb-1"
          >
            Destination Folder
          </label>
          <select
            id="destination-folder-select"
            value={selectedCollectionId}
            onChange={(e) => setSelectedCollectionId(e.target.value)}
            className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zinc-800 dark:text-zinc-100"
            disabled={isUploading}
          >
            {renderCollectionOptions(collectionTree)}
          </select>
        </div>

        <div className="mb-4">
          <CheckboxField>
            <Checkbox
              checked={autoSplitLarge}
              onChange={(checked) => setAutoSplitLarge(checked)}
              disabled={isUploading}
            />
            <Label>
              Automatically split large documents (&gt;100 pages)
            </Label>
          </CheckboxField>
        </div>

        {hasCompanions && (
          <div className="mb-4">
            <CompanionMatterPicker
              companions={selectableCompanions}
              selectedIds={selectedCompanionMatterIds}
              onChange={setSelectedCompanionMatterIds}
              label="Upload to companion matters"
              disabled={isUploading}
            />
          </div>
        )}

        <div
          className={`border-2 border-dashed rounded-md p-6 mb-4 text-center transition-colors ${
            isDragging ? 'border-primary bg-primary/5' : 'border-zinc-300 dark:border-zinc-600'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CloudArrowUpIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium">Drag and drop files here</p>
          <p className="text-xs text-muted-foreground mb-3">or</p>
          <Button
            onClick={handleFileSelect}
            disabled={isUploading}
            className="text-sm"
          >
            Browse Files
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            multiple
            className="hidden"
          />
        </div>

        {selectedFiles.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">
              Selected Files ({selectedFiles.length})
            </p>
            <div className="max-h-32 overflow-y-auto border border-zinc-200 dark:border-zinc-700 rounded-md">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between px-3 py-2 text-sm border-b border-zinc-200 dark:border-zinc-700 last:border-b-0"
                >
                  <span className="truncate max-w-[250px]">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    disabled={isUploading}
                    aria-label={`Remove ${file.name}`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isUploading}
          className="border border-zinc-300 dark:border-zinc-600"
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          disabled={isUploadDisabled}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
